import React from "react";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { theme } from "@src/theme";
import {
  analyzingResults,
  contentTesting,
  introductionToIntelligems,
  personalizationsGettingStarted,
  priceIntegrationDocs,
  shippingTesting,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";
import {
  ArrowUpRight,
  ChartLine,
  CircleHelp,
  type LucideIcon,
  Package,
  PencilRuler,
  Tag,
  Truck,
  UsersRound,
} from "lucide-react";

type ResourceArticle = {
  title: string;
  link: string;
  description: string;
  icon: LucideIcon;
};

const articleList: ResourceArticle[] = [
  {
    title: "Intro to Intelligems",
    link: introductionToIntelligems,
    description:
      "A quick tour of how testing and personalization compound into profit.",
    icon: CircleHelp,
  },
  {
    title: "Price Testing & Personalization",
    link: priceIntegrationDocs,
    description:
      "Real-time A/B tests on price to lift margin without bruising conversion.",
    icon: Tag,
  },
  {
    title: "Shipping Testing & Personalization",
    link: shippingTesting,
    description:
      "Tune rates and free-ship thresholds per audience to protect AOV.",
    icon: Truck,
  },
  {
    title: "Content Testing & Personalization",
    link: contentTesting,
    description:
      "Test landing pages, hero copy, imagery, and themes head-to-head.",
    icon: PencilRuler,
  },
  {
    title: "Audience Personalization",
    link: personalizationsGettingStarted,
    description:
      "Serve targeted offers, layouts, and copy to the visitors who'll convert.",
    icon: UsersRound,
  },
  {
    title: "Analyzing Test & Personalization Results",
    link: analyzingResults,
    description:
      "Cut by segment, export raw data, and turn experiments into decisions.",
    icon: ChartLine,
  },
];

const openResource = (article: ResourceArticle) => {
  openNewTab(article.link, article.title);
};

export const Resources = ({
  isStandardWidth,
}: {
  isStandardWidth: boolean;
}) => {
  return (
    <Stack direction={"column"} spacing={4}>
      <Grid
        container
        direction={"row"}
        columnSpacing={4}
        alignItems={"center"}
        rowSpacing={1}
      >
        <Grid
          sx={{
            maxWidth: { sm: "296px", lg: "none", md: "none", xs: "250px" },
          }}
          size={{
            md: 12,
            lg: "auto",
          }}
        >
          <IgTypography variant={"h2"} fontWeight={600}>
            Explore the levers that drive your biggest opportunities
          </IgTypography>
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems={"stretch"}>
        {articleList.map((article, index) => {
          const Icon = article.icon;

          return (
            <Grid
              onClick={() => openResource(article)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openResource(article);
                }
              }}
              role="button"
              tabIndex={0}
              key={`article-${index}`}
              sx={{
                display: "flex",
                outline: "none",
                "&:focus-visible > .resource-card": {
                  border: `1px solid ${theme.igBorders.core.hover}`,
                },
              }}
              size={{
                xs: 12,
                sm: 12,
                md: 6,
                lg: 4,
              }}
            >
              <IgPaper
                className="resource-card"
                sx={{
                  padding: 4,
                  border: `1px solid ${theme.igBorders.core.default}`,
                  borderRadius: "6px",
                  // ...(isStandardWidth && {
                  //   maxWidth: { md: "296px", sm: "296px", xs: "250px" },
                  // }),
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "120px",
                  overflow: "hidden",
                  "&:hover": {
                    cursor: "pointer",
                    border: `1px solid ${theme.igBorders.core.hover}`,
                  },
                }}
              >
                <Stack direction={"row"} spacing={3} alignItems={"flex-start"}>
                  <Stack
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: "6px",
                      padding: 2,
                      backgroundColor:
                        theme.igBackgroundColors.interactive.neutral.default,
                      // color: theme.palette.text.primary,
                    }}
                  >
                    <Icon size={18} />
                  </Stack>

                  <Stack spacing={3} sx={{ minWidth: 0 }}>
                    <Stack spacing={1}>
                      <IgTypography
                        fontWeight={600}
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: 14,
                        }}
                      >
                        {article.title}
                      </IgTypography>
                      <IgTypography
                        color={theme.palette.text.secondary}
                        sx={{
                          display: "block",
                          fontSize: 12,
                        }}
                      >
                        {article.description}
                      </IgTypography>
                    </Stack>

                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                      <IgTypography
                        variant={"body2"}
                        fontWeight={600}
                        sx={{
                          color: theme.palette.text.accent,
                        }}
                      >
                        Explore
                      </IgTypography>
                      <ArrowUpRight
                        size={14}
                        color={theme.palette.text.accent}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </IgPaper>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};
